import React, { useEffect, useState } from "react";
import { backend_url } from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaCheckCircle,
  FaBoxOpen,
  FaShippingFast,
  FaClipboardCheck,FaTrashAlt 
} from "react-icons/fa";
import { toast } from "react-toastify";

const statusIcons = {
  Confirmed: <FaClipboardCheck className="text-blue-500" />,
  Packed: <FaBoxOpen className="text-yellow-500" />,
  Shipped: <FaShippingFast className="text-orange-500" />,
  Delivered: <FaCheckCircle className="text-green-500" />,
};

const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const UserDetails = () => {
  const location = useLocation();
  const [user, setUser] = useState({ phoneNumber: "" });
  const [orders, setOrders] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "profile";
  const [activeTab, setActiveTab] = useState(initialTab);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserDetails = async () => {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${backend_url}/getuserdetails`, {
        headers: { "auth-token": token },
      });
      const data = await response.json();
      if (data.success) setUser(data.user);
    };

    const fetchOrderHistory = async (userId) => {
      try {
        const res = await fetch(`${backend_url}/orders/user/${userId}`);
        const data = await res.json();
        if (data.success) {
          setOrders(data.orders || []);
          console.log(data.orders);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      }
    };

    fetchUserDetails().then(() => {
      if (user?.userId) fetchOrderHistory(user.userId);
    });
  }, [backend_url, navigate, user?.userId]);

  const handleLogout = () => {
    localStorage.removeItem("auth-token");
    navigate("/login");
  };

  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    // update URL query param
    navigate(`/user-profile?tab=${tab}`, { replace: true });
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-full md:w-1/4 bg-white p-5 shadow-lg">
        <h2 className="text-2xl font-semibold text-purple-700 mb-6">
          Dashboard
        </h2>
        <nav className="space-y-3 md:space-y-4">
          <button
            onClick={() => setActiveTab("profile")}
            className={`block w-full text-left p-3 rounded-lg ${
              activeTab === "profile"
                ? "bg-purple-500 text-white"
                : "hover:bg-gray-200"
            }`}
          >
            Profile Details
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`block w-full text-left p-3 rounded-lg ${
              activeTab === "orders"
                ? "bg-purple-500 text-white"
                : "hover:bg-gray-200"
            }`}
          >
            Order History
          </button>
          <button
            onClick={() => setActiveTab("myOrders")}
            className={`block w-full text-left p-3 rounded-lg ${
              activeTab === "myOrders"
                ? "bg-purple-500 text-white"
                : "hover:bg-gray-200"
            }`}
          >
            My Orders
          </button>
          <button
            onClick={handleLogout}
            className="block w-full text-left p-3 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            Logout
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="w-full md:w-3/4 p-4 md:p-6">
        {activeTab === "profile" && (
          <ProfileSection
            user={user}
            editMode={editMode}
            setEditMode={setEditMode}
            setUser={setUser}
            saving={saving}
            setSaving={setSaving}
            errors={errors}
            setErrors={setErrors}
          />
        )}
        {activeTab === "orders" && <TrackOrderSection />}
        {activeTab === "myOrders" && (
          <OrdersTable user={user} backend_url={backend_url} />
        )}
      </div>
    </div>
  );
};

// ---------------------- PROFILE SECTION ----------------------
const ProfileSection = ({
  user,
  editMode,
  setEditMode,
  setUser,
  saving,
  setSaving,
  errors,
  setErrors,
}) => {
  const validatePhone = (value) => {
    if (!value) return "Phone number is required.";
    if (!/^\d+$/.test(value)) return "Only numbers allowed.";
    if (value.length !== 10) return "Phone must be 10 digits.";
    return "";
  };

  const handleSave = async () => {
    const errorMsg = validatePhone(user.phoneNumber);
    if (errorMsg) {
      setErrors({ phoneNumber: errorMsg });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("auth-token");

      const res = await fetch(`${backend_url}/saveuserdetails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "auth-token": token,
        },
        body: JSON.stringify(user),
      });

      const data = await res.json();
      if (data.success) setEditMode(false);
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 shadow-lg rounded-lg">
      <p className="text-sm text-gray-600 mb-4 bg-yellow-100 p-3 rounded border border-yellow-300">
        Please fill in your details accurately. These will be used to auto-fill
        your information when placing orders.
      </p>

      <div className="flex justify-between items-center border-b pb-4 mb-4">
        <h2 className="text-2xl font-semibold text-purple-700">
          Profile Details
        </h2>
        {editMode ? (
          <button
            className="bg-green-500 text-white px-4 py-2 rounded-md"
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        ) : (
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded-md"
            onClick={() => setEditMode(true)}
          >
            Edit
          </button>
        )}
      </div>

      <div className="grid gap-4">
        <ProfileField label="Name" value={user.name} />
        <ProfileField label="Email" value={user.email} />
        <ProfileField
          label="Phone Number"
          value={user.phoneNumber}
          editMode={editMode}
          error={errors.phoneNumber}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "");
            if (val.length <= 10) {
              setUser({ ...user, phoneNumber: val });
              setErrors({ phoneNumber: validatePhone(val) });
            }
          }}
        />
        <ProfileField
          label="Gender"
          value={user.gender}
          editMode={editMode}
          onChange={(e) => setUser({ ...user, gender: e.target.value })}
        />
        <ProfileField
          label="Address"
          value={user.address}
          editMode={editMode}
          onChange={(e) => setUser({ ...user, address: e.target.value })}
        />
        <ProfileField
          label="City"
          value={user.city}
          editMode={editMode}
          onChange={(e) => setUser({ ...user, city: e.target.value })}
        />
        <ProfileField
          label="Pincode"
          value={user.pincode}
          editMode={editMode}
          onChange={(e) =>
            setUser({
              ...user,
              pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
            })
          }
        />
        <ProfileField
          label="Country"
          isSelect={true}
          options={["India", "Other"]}
          value={user.country}
          editMode={editMode}
          onChange={(e) =>
            setUser({ ...user, country: e.target.value, state: "" })
          }
        />
        {user.country === "India" && (
          <ProfileField
            label="State"
            isSelect={true}
            options={indianStates}
            value={user.state}
            editMode={editMode}
            onChange={(e) => setUser({ ...user, state: e.target.value })}
          />
        )}
      </div>
    </div>
  );
};

// ---------------------- TRACK ORDER SECTION ----------------------
const TrackOrderSection = () => {
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTrackOrder = async () => {
    if (!orderId.trim()) {
      setError("Please enter a valid Order ID");
      return;
    }

    setLoading(true);
    setOrder(null);
    setError("");

    try {
      const res = await fetch(`${backend_url}/track-order/${orderId}`);
      const data = await res.json();
      if (data.success) {
        setOrder(data.order);
      } else {
        setError("Order not found.");
      }
    } catch {
      setError("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const getStepClass = (step) => {
    const steps = ["Confirmed", "Packed", "Shipped", "Delivered"];
    const current = steps.indexOf(order?.status || "");
    const index = steps.indexOf(step);
    return index <= current
      ? "border-green-400 bg-green-100 text-green-700"
      : "border-gray-300 bg-gray-100 text-gray-500";
  };

  //   return (
  //     <div className="bg-white p-6 shadow-lg rounded-lg">
  //       <h2 className="text-2xl font-semibold text-purple-700 mb-6">
  //         Track Your Order
  //       </h2>

  //       <div className="flex flex-col sm:flex-row gap-4 mb-4">
  //         <input
  //           type="text"
  //           value={orderId}
  //           onChange={(e) => setOrderId(e.target.value)}
  //           placeholder="Enter Order ID"
  //           className="w-full sm:w-2/3 border p-3 rounded-md"
  //         />
  //         <button
  //           onClick={handleTrackOrder}
  //           className="bg-purple-600 text-white px-4 py-2 rounded-md"
  //         >
  //           {loading ? "Tracking..." : "Track Order"}
  //         </button>
  //       </div>

  //       {error && <p className="text-red-500">{error}</p>}

  //       {order && (
  //         <div>
  //           {/* 🧾 Order Details */}
  //           <div className="grid sm:grid-cols-2 gap-4 mb-6">
  //             <p><strong>Order ID:</strong> {order._id}</p>
  //             <p><strong>Name:</strong> {order.name}</p>
  //             <p><strong>Price:</strong> ₹{order.totalPrice}</p>
  //             <p><strong>Date:</strong> {new Date(order.date).toLocaleDateString()}</p>
  //           </div>

  //           {/* 📦 Product Details */}
  //           {order.product && (
  //             <div className="border p-4 rounded-lg mb-6 bg-gray-50">
  //               <h3 className="text-lg font-semibold mb-2 text-purple-700">Product Details</h3>
  //               <p><strong>Product Name:</strong> {order.product.productName}</p>
  //               <p><strong>Quantity:</strong> {order.product.quantity}</p>
  //               <img
  //   src={order.product?.image || "/placeholder.png"}
  //   alt={order.product?.productName || "Product"}
  //   className="w-24 h-24 rounded-lg object-cover border"
  // />
  //             </div>
  //           )}

  //           {/* 🚚 Status Timeline */}
  //           <div className="overflow-x-auto">
  //             <div className="flex justify-between items-center gap-2 min-w-[600px]">
  //               {["Confirmed", "Packed", "Shipped", "Delivered"].map((step, idx) => (
  //                 <div key={step} className="flex flex-col items-center w-1/4 relative">
  //                   <div
  //                     className={`w-10 h-10 flex items-center justify-center rounded-full border-4 text-xl z-10 ${getStepClass(step)}`}
  //                   >
  //                     {statusIcons[step]}
  //                   </div>
  //                   <span
  //                     className={`mt-2 text-sm ${
  //                       order.status === step ? "text-purple-700" : "text-gray-500"
  //                     }`}
  //                   >
  //                     {step}
  //                   </span>
  //                   {idx < 3 && (
  //                     <div className="absolute top-5 right-0 w-full h-1 bg-gray-300 z-0">
  //                       <div
  //                         className={`h-1 ${
  //                           ["Confirmed", "Packed", "Shipped", "Delivered"].indexOf(order.status) > idx
  //                             ? "bg-green-400"
  //                             : ""
  //                         }`}
  //                         style={{ width: "100%" }}
  //                       ></div>
  //                     </div>
  //                   )}
  //                 </div>
  //               ))}
  //             </div>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );

  return (
    <div className="bg-gradient-to-br from-purple-50 to-white p-8 shadow-xl rounded-2xl">
      <h2 className="text-3xl font-bold text-purple-700 mb-8 text-center">
        🚚 Track Your Order
      </h2>

      {/* 🔎 Input + Button */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="Enter Order ID"
          className="w-full sm:w-2/3 border p-3 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
        />
        <button
          onClick={handleTrackOrder}
          className="bg-purple-600 hover:bg-purple-700 transition text-white px-6 py-3 rounded-lg shadow-md"
        >
          {loading ? "Tracking..." : "Track Order"}
        </button>
      </div>

      {error && <p className="text-red-500 font-medium">{error}</p>}

      {order && (
        <div className="mt-6 space-y-6">
          {/* 🧾 Order Details */}
          <div className="grid sm:grid-cols-2 gap-4 text-gray-700">
            <p>
              <span className="font-semibold text-gray-900">Order ID:</span>{" "}
              {order._id}
            </p>
            <p>
              <span className="font-semibold text-gray-900">Name:</span>{" "}
              {order.name}
            </p>
            <p>
              <span className="font-semibold text-gray-900">Price:</span> ₹
              {order.totalPrice}
            </p>
            <p>
              <span className="font-semibold text-gray-900">Date:</span>{" "}
              {new Date(order.date).toLocaleDateString()}
            </p>
          </div>

          {/* 📦 Product Details */}
          {order.product && (
            <div className="border p-5 rounded-xl bg-white shadow-md hover:shadow-lg transition">
              <h3 className="text-xl font-semibold mb-4 text-purple-700">
                Product Details
              </h3>
              <div className="flex items-center gap-4">
                <img
                  src={order.product?.image || "/placeholder.png"}
                  alt={order.product?.productName || "Product"}
                  className="w-28 h-28 rounded-lg object-cover shadow-md border"
                />
                <div>
                  <p className="text-lg font-medium">
                    {order.product.productName}
                  </p>
                  <p className="text-gray-600">
                    Quantity: {order.product.quantity}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 🚚 Status Timeline */}
          <div className="overflow-x-auto py-6">
            <div className="flex justify-between items-center gap-2 min-w-[600px] relative">
              {["Confirmed", "Packed", "Shipped", "Delivered"].map(
                (step, idx) => {
                  const steps = ["Confirmed", "Packed", "Shipped", "Delivered"];
                  const currentIndex = steps.indexOf(order.status);
                  const isCompleted = idx < currentIndex;
                  const isActive = idx === currentIndex;

                  return (
                    <div
                      key={step}
                      className="flex flex-col items-center w-1/4 relative"
                    >
                      {/* 🔗 Connector Line */}
                      {idx < steps.length - 1 && (
                        <div className="absolute top-6 left-1/2 h-1 w-full -translate-x-0 bg-gray-300">
                          <div
                            className={`h-1 transition-all duration-700 ease-in-out ${
                              isCompleted
                                ? "bg-gradient-to-r from-green-400 to-purple-500"
                                : ""
                            }`}
                            style={{
                              width: isCompleted
                                ? "100%"
                                : isActive
                                ? "50%"
                                : "0%",
                            }}
                          ></div>
                        </div>
                      )}

                      {/* ⭕ Step Circle */}
                      <div
                        className={`w-12 h-12 flex items-center justify-center rounded-full border-4 text-xl font-bold transition-all duration-500 z-10
              ${
                isCompleted
                  ? "bg-gradient-to-r from-green-400 to-purple-500 text-white border-green-400"
                  : isActive
                  ? "bg-purple-100 border-purple-500 text-purple-600 animate-pulse"
                  : "bg-gray-100 border-gray-300 text-gray-400"
              }`}
                      >
                        {statusIcons[step]}
                      </div>

                      {/* 🏷 Step Label */}
                      <span
                        className={`mt-3 text-sm font-semibold transition-all duration-300 ${
                          isCompleted
                            ? "text-green-600"
                            : isActive
                            ? "text-purple-700"
                            : "text-gray-400"
                        }`}
                      >
                        {step}
                      </span>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OrdersTable = ({ user, backend_url }) => {
  const [myOrders, setMyOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);

  useEffect(() => {
    const fetchUserOrders = async () => {
      try {
        const res = await fetch(`${backend_url}/orders/user/${user.userId}`);
        const data = await res.json();
        if (data.success) {
          setMyOrders(data.orders || []);
        } else {
          console.error("Failed to fetch orders:", data.message);
        }
      } catch (error) {
        console.error("Error fetching user orders:", error);
      }
    };

    if (user?.userId) {
      fetchUserOrders();
    }
  }, [backend_url, user?.userId]);

  const getDeliveryDate = (orderDate) => {
    if (!orderDate) return "N/A";
    const date = new Date(orderDate);
    date.setDate(date.getDate() + 5);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleCancel = async (orderId) => {
    const confirmCancel = window.confirm(
      "Are you sure you want to cancel this order?"
    );

    if (!confirmCancel) return;

    try {
      const res = await fetch(`${backend_url}/edit-order/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Cancelled" }),
      });

      const data = await res.json();
      if (data.success) {
        toast.success("Order cancelled successfully.");
        setMyOrders((prev) =>
          prev.map((order) =>
            order._id === orderId ? { ...order, status: "Cancelled" } : order
          )
        );
      } else {
        toast.error(data.message || "Failed to cancel order.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    }
  };

  // Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = myOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(myOrders.length / ordersPerPage);

  return (
    <div className="p-6 bg-[#F3F4F6] min-h-screen">
      <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-semibold text-purple-700 mb-6">
          My Orders
        </h2>

        {myOrders.length === 0 ? (
          <p className="text-gray-500">No orders found.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-purple-300">
                  <tr>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">
                      Order ID
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">
                      Product
                    </th>
                    <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">
                      Price
                    </th>
                    <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">
                      Quantity
                    </th>
                    <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">
                      Delivery Date
                    </th>
                    <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentOrders.map((order, idx) => (
                    <tr
                      key={order._id}
                      className={`border-b hover:bg-gray-50 transition ${
                        idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {order._id.slice(-7).toUpperCase()}
                      </td>
                      <td className="py-3 px-4 flex items-center gap-3">
                        <img
                          crossOrigin="anonymous"
                          src={
                            order.productId?.images?.[0] ||
                            "https://via.placeholder.com/100"
                          }
                          alt={order.productId?.name || "Product"}
                          className="w-16 h-16 object-cover rounded"
                        />
                        <span className="text-gray-800 text-sm">
                          {order.productId?.name || "Unknown Product"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-700">
                        ₹{order.pricePerUnit}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-700">
                        {order.quantity}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-700">
                        {getDeliveryDate(order.orderDate)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            order.status === "Pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : order.status === "Confirmed"
                              ? "bg-blue-100 text-blue-700"
                              : order.status === "Packed"
                              ? "bg-purple-100 text-purple-700"
                              : order.status === "Shipped"
                              ? "bg-indigo-100 text-indigo-700"
                              : order.status === "Out for Delivery"
                              ? "bg-orange-100 text-orange-700"
                              : order.status === "Delivered"
                              ? "bg-green-100 text-green-700"
                              : order.status === "Cancelled"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {order.status}
                        </span>

                        {order.status !== "Cancelled" &&
                          order.status !== "Delivered" && (
                            <button
                              onClick={() => {
                                setCancelOrderId(order._id);
                                setShowCancelModal(true);
                              }}
                              className="ml-2 text-red-500 text-xs underline text-center mt-3"
                            >
                              Cancel
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showCancelModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4">
                <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl flex flex-col overflow-hidden">
                  {/* Header */}
                  <div className="flex justify-between items-center p-6 bg-purple-300">
                    <h2 className="text-2xl font-bold text-white">
                      Cancel Order
                    </h2>
                    <button
                      className="text-white text-2xl font-bold hover:text-gray-200"
                      onClick={() => setShowCancelModal(false)}
                    >
                      ×
                    </button>
                  </div>

                  {/* Info content */}
                  <div className="p-6 overflow-y-auto flex-1 space-y-3 text-gray-800 text-sm max-h-[60vh]">
                    <p>
                      💰 <strong>Refunds:</strong> Approved refunds are
                      processed to the original payment method within 5–15
                      business days.
                    </p>
                    <p>
                      📦 <strong>Wrong product:</strong> If you received the
                      wrong product, we will replace it at no extra cost after
                      verification.
                    </p>
                    <p>
                      ⏰ <strong>Request time:</strong> Request must be raised
                      within 7 days of delivery.
                    </p>
                    <p>
                      🧵 <strong>Fabric condition:</strong> Must be unused,
                      unwashed, unstitched, and in the original cut length.
                    </p>
                    <p>
                      📄 <strong>Return items:</strong> Include original tags,
                      invoice, and packaging when returning.
                    </p>
                    <p>
                      ⚠️ <strong>Exclusions:</strong> Custom-printed fabrics and
                      specially-tailored orders are not eligible for return.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-4 p-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowCancelModal(false)}
                      className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
                    >
                      Close
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(
                            `${backend_url}/edit-order/${cancelOrderId}`,
                            {
                              method: "PUT",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ status: "Cancelled" }),
                            }
                          );
                          const data = await res.json();
                          if (data.success) {
                            toast.success("Order cancelled successfully.");
                            setMyOrders((prev) =>
                              prev.map((order) =>
                                order._id === cancelOrderId
                                  ? { ...order, status: "Cancelled" }
                                  : order
                              )
                            );
                          } else {
                            toast.error(
                              data.message || "Failed to cancel order."
                            );
                          }
                        } catch (err) {
                          console.error(err);
                          toast.error("Something went wrong.");
                        } finally {
                          setShowCancelModal(false);
                          setCancelOrderId(null);
                        }
                      }}
                      className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700"
                    >
                      Confirm Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Pagination Controls */}
            <div className="flex justify-center items-center gap-2 mt-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded ${
                    currentPage === i + 1
                      ? "bg-purple-500 text-white"
                      : "bg-gray-200"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------- PROFILE FIELD ----------------------
const ProfileField = ({
  label,
  value,
  editMode,
  onChange,
  error,
  type = "text",
  isSelect = false,
  options = [],
}) => (
  <div className="flex flex-col">
    <label className="text-gray-600 font-medium">{label}:</label>
    {editMode ? (
      isSelect ? (
        <select
          value={value || ""}
          onChange={onChange}
          className={`border p-2 rounded-md ${error ? "border-red-500" : ""}`}
        >
          <option value="">Select {label}</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={onChange}
          className={`border p-2 rounded-md ${error ? "border-red-500" : ""}`}
        />
      )
    ) : (
      <span className="text-gray-800">{value || "N/A"}</span>
    )}
    {error && <span className="text-red-500 text-sm">{error}</span>}
  </div>
);

export default UserDetails;
